import React, { useContext, useState } from 'react'
import API from '../../services/API'
import DocumentPreviewModal from './DocumentPreviewModal'
import { GlobalContext } from '../../App'

export const IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'gif', 'webp', 'jfif']

export const extensionOf = (name) =>
  typeof name === 'string' && name.includes('.')
    ? name.split('.').pop().toLowerCase()
    : ''

const baseName = (key) => key?.split('/').pop()

// In-app preview for a document stored in S3. Call `openPreview` with
// `{ key, name }` - `name` is optional, but stored keys are random UUIDs, so
// pass the original file name whenever the record kept one - and render
// `previewModal` once in the tree:
//
//   const { openPreview, previewModal } = useDocumentPreview()
//   <Button onClick={() => openPreview(invoice.file)}>View</Button>
//   {previewModal}
const useDocumentPreview = () => {
  const { setToast } = useContext(GlobalContext)
  const [preview, setPreview] = useState(null)

  // The API client never rejects on 4xx/5xx - check the status
  const getDownloadUrl = async (key, inlineView, filename) => {
    const res = await API.post('/gets3downloadurl', {
      key,
      inlineView,
      filename,
    })
    if (res.status === 200 && res.data) return res.data
    setToast({ message: 'Unable to open this document', severity: 'error' })
    return null
  }

  // `actions` is an optional node rendered beside the modal's header buttons
  // (e.g. an Edit button for the previewed document)
  const openPreview = async ({ key, name, actions } = {}) => {
    if (!key) return
    const url = await getDownloadUrl(key, true, name)
    if (url) setPreview({ key, name, url, actions })
  }

  const closePreview = () => setPreview(null)

  const download = async () => {
    const fileName = preview.name || baseName(preview.key)
    const url = await getDownloadUrl(preview.key, false, fileName)
    if (!url) return
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const extension = extensionOf(preview?.name || preview?.key)

  const previewModal = (
    <DocumentPreviewModal
      open={!!preview}
      onClose={() => setPreview(null)}
      fileUrl={preview?.url}
      fileName={preview?.name || baseName(preview?.key)}
      originalFileName={preview?.name}
      fileExt={extension}
      isImage={IMAGE_EXTENSIONS.includes(extension)}
      isPdf={extension === 'pdf'}
      onDownload={download}
      actions={preview?.actions}
    />
  )

  return { openPreview, closePreview, previewModal }
}

export default useDocumentPreview
